// Mouse pointer and wheel (ui-flow §11.1–11.2): the pointer position over the canvas (the
// steering target is computed from it every frame by the input sender), wheel zoom notches, and
// Ctrl/⌘+wheel page zoom blocked over the canvas. Clicks do nothing. No pointer lock.
//
// Details (decided here):
// - The pointer is tracked on the whole window (relative to the canvas), so steering continues
//   while the cursor is over a HUD element that takes pointer events. Touch pointers are ignored
//   (input/touch.ts steers on touch screens).
// - Until the first pointer event, `x`/`y` are the canvas centre (the cell then holds still
//   instead of heading for the top-left corner).
// - Ctrl/⌘+wheel (a trackpad pinch on macOS) zooms the game like the wheel; page zoom is blocked.
// - One event yields at most ±3 notches, so a fast trackpad flick cannot jump the whole range.
//
// Implementer: client input/UI. Consumers: app.ts (wheel → camera.zoomBy; pointer → InputSender).

import { Emitter } from '../net/socket.js';

export interface MouseEvents {
  /** Wheel notches: +1 per notch up (zoom in), −1 down; fractional for smooth wheels. */
  wheel: { readonly notches: number };
}

/** About one wheel notch, in pixels (ui-flow §11.1). */
export const WHEEL_NOTCH_PX = 100;

/** WheelEvent.deltaMode values. */
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/** One notch is 3 lines in line mode (Firefox), and one page in page mode. */
const LINE_PX = WHEEL_NOTCH_PX / 3;
const PAGE_PX = WHEEL_NOTCH_PX;

/** Largest notch count one wheel event may produce. */
export const MAX_NOTCHES_PER_EVENT = 3;

/**
 * Normalised wheel notches (ui-flow §11.1): `deltaMode` lines/pages converted to pixels, one
 * notch ≈ 100 px, sign flipped so scrolling up is positive.
 */
export function wheelNotches(deltaY: number, deltaMode: number): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) return 0;
  const px =
    deltaMode === DOM_DELTA_LINE
      ? deltaY * LINE_PX
      : deltaMode === DOM_DELTA_PAGE
        ? deltaY * PAGE_PX
        : deltaY;
  const n = -px / WHEEL_NOTCH_PX;
  return n > MAX_NOTCHES_PER_EVENT
    ? MAX_NOTCHES_PER_EVENT
    : n < -MAX_NOTCHES_PER_EVENT
      ? -MAX_NOTCHES_PER_EVENT
      : n;
}

export class MouseInput {
  readonly events = new Emitter<MouseEvents>();
  /** Pointer position in CSS px relative to the canvas's top-left (last known). */
  x = 0;
  y = 0;
  /** A pointer has moved over the canvas at least once. */
  hasPointer = false;
  protected readonly canvas: HTMLCanvasElement;
  private win: Window | null = null;
  private readonly onPointer = (ev: PointerEvent): void => this.pointer(ev);
  private readonly onWheel = (ev: WheelEvent): void => this.wheel(ev);
  private readonly onResize = (): void => {
    if (!this.hasPointer) this.centre();
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  attach(): void {
    if (this.win) return;
    const win = this.canvas.ownerDocument?.defaultView ?? null;
    if (!win) return;
    this.win = win;
    win.addEventListener('pointermove', this.onPointer, { passive: true });
    win.addEventListener('pointerdown', this.onPointer, { passive: true });
    win.addEventListener('resize', this.onResize);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    if (!this.hasPointer) this.centre();
  }

  detach(): void {
    const win = this.win;
    if (!win) return;
    this.win = null;
    win.removeEventListener('pointermove', this.onPointer);
    win.removeEventListener('pointerdown', this.onPointer);
    win.removeEventListener('resize', this.onResize);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }

  // -------------------------------------------------------------------------------------------

  private centre(): void {
    this.x = (this.canvas.clientWidth || 0) / 2;
    this.y = (this.canvas.clientHeight || 0) / 2;
  }

  private pointer(ev: PointerEvent): void {
    if (ev.pointerType === 'touch') return;
    const rect = this.canvas.getBoundingClientRect();
    this.x = ev.clientX - rect.left;
    this.y = ev.clientY - rect.top;
    this.hasPointer = true;
  }

  private wheel(ev: WheelEvent): void {
    // The canvas never scrolls; this also blocks Ctrl/⌘+wheel page zoom over it.
    if (ev.cancelable) ev.preventDefault();
    const n = wheelNotches(ev.deltaY, ev.deltaMode);
    if (n !== 0) this.events.emit('wheel', { notches: n });
  }
}
